import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import * as Sentry from '@sentry/react';
import { doc, onSnapshot, type FirestoreErrorCode } from 'firebase/firestore';
import type { UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';

import { useServices } from '@/providers/services-provider';
import { userAccountAtom } from '@/state';

/** Mounted once per session so every reader (the Energy chip, the billing page) gets a loaded account without a per-mount subscription; costs one live listener per tab. */
export const useUserAccountListener = (userId: string) => {
  const { firestore } = useServices();
  const setUserAccount = useSetAtom(userAccountAtom);

  useEffect(() => {
    if (!firestore || !userId) return;

    let unsubscribe: (() => void) | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;

    const subscribe = () => {
      unsubscribe = onSnapshot(
        doc(firestore, 'userAccounts', userId),
        (snapshot) => {
          if (!snapshot.metadata.fromCache) failures = 0;
          // A cached snapshot without the doc is not loaded yet; Free is only known from the server.
          if (snapshot.metadata.fromCache && !snapshot.exists()) return;
          setUserAccount({ account: snapshot.data() as UserAccountDoc | undefined, loading: false });
        },
        // Stay loading: falling back to Free would offer Checkout to a subscriber. A failed listener
        // stops for good, so subscribe again after a backoff, on transient errors only. A revoked
        // session is sent to logout by services-provider's onAuthStateChanged handler.
        (error) => {
          Sentry.captureException(error);
          if (!TRANSIENT_ERROR_CODES.has(error.code)) return;
          retryTimer = setTimeout(subscribe, Math.min(RETRY_BASE_MS * 2 ** failures, RETRY_MAX_MS));
          failures += 1;
        }
      );
    };

    subscribe();
    return () => {
      clearTimeout(retryTimer);
      unsubscribe?.();
      // A new user or Firestore instance never shows the previous account.
      setUserAccount({ account: undefined, loading: true });
    };
  }, [firestore, userId, setUserAccount]);
};

const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 60_000;
const TRANSIENT_ERROR_CODES = new Set<FirestoreErrorCode>([
  'unavailable',
  'deadline-exceeded',
  'internal',
  'resource-exhausted',
  'aborted',
  'unknown',
]);
